import { useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { motion, useReducedMotion } from 'framer-motion'

type Point = { x: number; y: number }

const add = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y })
const scale = (a: Point, k: number): Point => ({ x: a.x * k, y: a.y * k })
const fmt = (p: Point) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`

/** The rendered one of several elements (the others are display:none at this breakpoint). */
function visibleRect(refs: RefObject<HTMLElement | null>[]): DOMRect | null {
  for (const ref of refs) {
    const el = ref.current
    if (el && el.getClientRects().length > 0) return el.getBoundingClientRect()
  }
  return null
}

/**
 * A hand-drawn arrow from the message to the target: a swoop with a small loop in the middle,
 * ending in an open arrowhead. Coordinates are in viewport space (the SVG is position: fixed).
 */
function scribblePath(from: DOMRect, to: DOMRect) {
  // Target below the message (the phone's bottom bar): leave from the message's bottom edge.
  // Otherwise (the desktop side panel, up and to the left): leave from its left edge.
  const below = to.top > from.bottom
  const s: Point = below
    ? { x: from.left + from.width / 2, y: from.bottom + 16 }
    : { x: from.left - 16, y: from.top + from.height * 0.35 }
  const e: Point = below
    ? { x: to.left + to.width / 2, y: to.top - 12 }
    : { x: to.right + 8, y: to.bottom + 6 }

  const chord = { x: e.x - s.x, y: e.y - s.y }
  const dist = Math.hypot(chord.x, chord.y) || 1
  const d = scale(chord, 1 / dist)
  // Bow toward the lower side so the arrow swoops under, then up into the target.
  let n: Point = { x: -d.y, y: d.x }
  if (n.y < 0 || (n.y === 0 && n.x > 0)) n = scale(n, -1)
  const bow = Math.min(Math.max(dist * 0.2, 18), 110)
  const k = dist * 0.2
  const r = Math.min(Math.max(dist * 0.06, 12), 20)

  const m = add(add(s, scale(chord, 0.5)), scale(n, bow))
  // Loop-de-loop: a self-crossing cubic in the (d, n) frame. The swoops on either side meet it
  // along its own start and end tangents, so the stroke stays smooth through the loop.
  const local = (u: number, v: number) => add(m, add(scale(d, u * r), scale(n, v * r)))
  const m2 = local(1, 0)
  const unit = (p: Point) => scale(p, 1 / (Math.hypot(p.x, p.y) || 1))
  const loopIn = unit(add(scale(d, 2.4), scale(n, 2)))
  const loopOut = unit(add(scale(d, 2.4), scale(n, -2)))
  const c3 = add(add(e, scale(n, bow * 0.7)), scale(d, -dist * 0.12))

  const line = [
    `M ${fmt(s)}`,
    `C ${fmt(add(add(s, scale(n, bow * 0.7)), scale(d, dist * 0.12)))} ${fmt(add(m, scale(loopIn, -k)))} ${fmt(m)}`,
    `C ${fmt(local(2.4, 2))} ${fmt(local(-1.4, 2))} ${fmt(m2)}`,
    `C ${fmt(add(m2, scale(loopOut, k)))} ${fmt(c3)} ${fmt(e)}`,
  ].join(' ')

  // Arrowhead: two strokes swept back from the tip along the arrival direction.
  const tx = e.x - c3.x, ty = e.y - c3.y
  const tl = Math.hypot(tx, ty) || 1
  const back = { x: -tx / tl, y: -ty / tl }
  const barb = (deg: number) => {
    const a = (deg * Math.PI) / 180
    return add(e, scale({ x: back.x * Math.cos(a) - back.y * Math.sin(a), y: back.x * Math.sin(a) + back.y * Math.cos(a) }, 13))
  }
  const head = `M ${fmt(barb(32))} L ${fmt(e)} L ${fmt(barb(-26))}`

  return { line, head }
}

function ScribbleArrow({ fromRef, targetRefs }: {
  fromRef: RefObject<HTMLElement | null>
  targetRefs: RefObject<HTMLElement | null>[]
}) {
  const reduceMotion = useReducedMotion()
  const [paths, setPaths] = useState<{ line: string; head: string } | null>(null)

  useLayoutEffect(() => {
    function measure() {
      const from = fromRef.current?.getBoundingClientRect()
      const to = visibleRect(targetRefs)
      setPaths(from && to ? scribblePath(from, to) : null)
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (fromRef.current) observer.observe(fromRef.current)
    for (const ref of targetRefs) if (ref.current) observer.observe(ref.current)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
    // Refs are stable; the caller passes a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromRef])

  if (!paths) return null

  return createPortal(
    <svg className="fixed inset-0 w-full h-full pointer-events-none z-30 text-muted-foreground/70" aria-hidden>
      <g fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <motion.path
          d={paths.line}
          initial={reduceMotion ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.65, 0, 0.35, 1] }}
        />
        <motion.path
          d={paths.head}
          initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay: 1.1, ease: 'easeOut' }}
        />
      </g>
    </svg>,
    document.body,
  )
}

/** Home content for an account with no life areas yet: a nudge toward the "new life area" button. */
export function EmptyAreasState({ targetRefs }: { targetRefs: RefObject<HTMLElement | null>[] }) {
  const messageRef = useRef<HTMLDivElement>(null)

  return (
    <div className="flex items-center justify-center h-full px-6 pb-24 md:pb-0">
      <div ref={messageRef} className="max-w-xs text-center space-y-2">
        <h2 className="font-serif text-2xl tracking-tight">A blank slate.</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Start by adding a life area — a part of your life you want to tend, like family, work, or health.
        </p>
      </div>
      <ScribbleArrow fromRef={messageRef} targetRefs={targetRefs} />
    </div>
  )
}
